/* eslint-disable max-lines */
/**
 * ProjectTerritorialScene — Bloque 1.6 / 2.0 / 3B.1
 *
 * Modo de escena único (sceneMode) — elimina la combinación theme + terrainEnabled.
 *   dark        → streets-v2-dark,  sin terrain, red cyan.
 *   light       → streets-v2-light, sin terrain, red turquesa.
 *   topographic → outdoor-v2,       terrain + hillshade activo, red turquesa por encima.
 *   satellite   → MapTiler hybrid,  terrain + atmósfera blue-hour, red dorada sintáctica.
 *   cover       → Esri World Imagery inline, terrain moderado, red dorada luminosa — portada visual.
 *
 * Orden crítico en style.load para topographic/satellite/cover:
 *   1. ensureTerrain → hillshade al fondo.
 *   2. addDataLayers → red por encima del hillshade.
 *
 * Cámara inicial (prioridad):
 *   1. ENTRY_VIEW_REGISTRY[projectId] — provisional, pendiente backend.
 *   2. resolveTerritorialEntryCamera  — algoritmo adaptable.
 */
import type { FeatureCollection, LineString, MultiLineString, Point, Position } from 'geojson';
import maplibregl from 'maplibre-gl';
import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '../../auth';
import { mapStyleService } from '../../map-engine/MapStyleService';
import { useProjectCanonicalNetworkDisplay } from '../hooks/useProjectCanonicalNetworkDisplay';
import type {
  PersistedView,
  ProjectBounds,
  TerritorialCamera,
} from '../utils/territorialEntryCamera';
import { resolveTerritorialEntryCamera } from '../utils/territorialEntryCamera';

export type SceneMode = 'dark' | 'light' | 'topographic' | 'satellite' | 'cover';

const STYLE_SWITCH_ENABLED: boolean = true;

// ─── Performance logging ──────────────────────────────────────────────────────
// Activar en consola: localStorage.setItem('westeer:territoryPerf', '1')
// Desactivar:        localStorage.removeItem('westeer:territoryPerf')
const PERF_ENABLED =
  import.meta.env.DEV &&
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('westeer:territoryPerf') === '1';

function perfMark(label: string): void {
  if (!PERF_ENABLED) return;
  const ms = Math.round(performance.now());
  performance.mark(`territory:${label}`);
  // eslint-disable-next-line no-console
  console.debug(`[ts:perf] +${ms}ms  ${label}`);
}

function perfMeasure(label: string, from: string, to: string): void {
  if (!PERF_ENABLED) return;
  try {
    const m = performance.measure(label, `territory:${from}`, `territory:${to}`);
    // eslint-disable-next-line no-console
    console.debug(`[ts:perf] ⏱  ${label}`, Math.round(m.duration), 'ms');
  } catch {
    /* marks pueden no existir en todos los paths */
  }
}

interface ProjectTerritorialSceneProps {
  projectId: string | null;
  bounds: ProjectBounds;
  sceneMode: SceneMode;
  onSceneReady?: () => void;
  onSceneError?: () => void;
}

// ─── Layer / source IDs ───────────────────────────────────────────────────────

const SOURCE_ID = 'territorial-network-preview';
const SOURCE_NODES_ID = 'territorial-network-nodes';
const LAYER_HALO_ID = 'territorial-network-halo';
const LAYER_GLOW_TIGHT_ID = 'territorial-network-glow-tight';
const LAYER_ID = 'territorial-network-lines';
const LAYER_NODE_GLOW_BASE_ID = 'territorial-network-node-glow-base';
const LAYER_NODE_GLOW_UP_ID = 'territorial-network-node-glow-up';
const LAYER_NODE_CORE_ID = 'territorial-network-node-core';
const LAYER_SAT_ATMOSPHERE_ID = 'territorial-sat-atmosphere'; // velo azul-gris entre basemap y red
const LAYER_HILLSHADE_ID = 'territorial-terrain-hillshade';
const FADE_IN_MS = 600;
const TARGET_OPACITY_LINES = 1;
const TERRAIN_SOURCE_ID = 'territory-dem-terrain';
const HILLSHADE_SOURCE_ID = 'territory-dem-hillshade';
const MAPTILER_DEM_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined) ?? '';
const DEFAULT_MAX_PITCH = 60;
const NODE_COORD_PRECISION = 4;

// ─── Esri World Imagery — estilo inline para modo cover ──────────────────────
// Referencia estable (constante de módulo) para que useSceneSync pueda comparar
// prevStyle === nextStyle sin falsos positivos por objetos nuevos cada render.
// No requiere VITE_MAPTILER_API_KEY. Atribución según ToS de Esri.
const ESRI_COVER_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  name: 'WESTEER Cover — Esri World Imagery',
  sources: {
    'esri-world-imagery': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 512,
      maxzoom: 19,
      attribution:
        '© Esri, Maxar, Earthstar Geographics, CNES/Airbus DS, USDA FSA, USGS, Aerogrid, IGN',
    },
  },
  layers: [
    {
      id: 'esri-world-imagery',
      type: 'raster',
      source: 'esri-world-imagery',
    },
  ],
};

// ─── Syntax API ───────────────────────────────────────────────────────────────

const SYNTAX_API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8080/api/v1';
// Fallback bajo: segmentos sin dato deben quedar visibles pero claramente subordinados.
const SYNTAX_STRUCTURE_FALLBACK = 0.1;

// Activa logs de diagnóstico de sintaxis en modo DEV. Dejar en false por defecto.
const DEBUG_TERRITORIAL_SYNTAX = false;

// Diagnóstico de visibilidad de capas en runtime — loguea layers, paint y scores.
// Dejar en false en producción.
const DEBUG_TERRITORIAL_NETWORK_VISIBILITY = false;
// Emergencia visual: renderiza toda la red plana y muy visible, ignora syntaxStructureScore.
// Activar para distinguir si el problema es datos/layers/orden vs. rampa de estilo.
// Dejar en false en producción.
const FORCE_TERRITORIAL_NETWORK_VISIBLE = false;

// Modo navegación libre — desactiva red y fetches pesados, muestra overlay de cámara.
// Activar para calibrar posición de cámara sin carga. Desactivar para render final.
const NAVIGATION_MODE = false;

type TerritorialCameraPresetName =
  | 'current'
  | 'lowOblique'
  | 'cinematicBalanced'
  | 'deepHorizon'
  | 'territorialWide';

interface TerritorialCameraPreset {
  centerOffset: readonly [number, number];
  zoomDelta: number;
  pitch: number;
  bearingDelta: number;
  maxPitch: number;
  terrainExaggeration: number;
}

interface SatelliteAtmospherePreset {
  skyColor: string;
  horizonColor: string;
  fogColor: string;
  fogGroundBlend: number;
  horizonFogBlend: number;
  skyHorizonBlend: number;
  atmosphereBlend: number;
}

const TERRITORIAL_CAMERA_PRESET: TerritorialCameraPresetName = 'cinematicBalanced';

const TERRITORIAL_CAMERA_PRESETS: Record<TerritorialCameraPresetName, TerritorialCameraPreset> = {
  // Preserva exactamente la cámara satelital actual:
  // center [-68.536, -31.537], zoom 12.4, pitch 68, bearing -22, maxPitch 75.
  current: {
    centerOffset: [0, 0],
    zoomDelta: 0,
    pitch: 68,
    bearingDelta: 0,
    maxPitch: 75,
    terrainExaggeration: 1.0,
  },
  lowOblique: {
    centerOffset: [0.012, -0.016],
    zoomDelta: -0.18,
    pitch: 66,
    bearingDelta: -8,
    maxPitch: 78,
    terrainExaggeration: 1.15,
  },
  cinematicBalanced: {
    centerOffset: [-0.006, 0.01],
    zoomDelta: -0.42,
    pitch: 74.5,
    bearingDelta: -14,
    maxPitch: 85,
    terrainExaggeration: 1.26,
  },
  deepHorizon: {
    centerOffset: [0.018, -0.042],
    zoomDelta: -0.52,
    pitch: 76,
    bearingDelta: -16,
    maxPitch: 82,
    terrainExaggeration: 1.32,
  },
  territorialWide: {
    centerOffset: [0.026, -0.026],
    zoomDelta: -0.82,
    pitch: 70,
    bearingDelta: -12,
    maxPitch: 80,
    terrainExaggeration: 1.2,
  },
};

const SATELLITE_ATMOSPHERE_PRESETS: Record<TerritorialCameraPresetName, SatelliteAtmospherePreset> =
  {
    current: {
      skyColor: '#0d1f3f',
      horizonColor: '#45658e',
      fogColor: 'rgb(48, 67, 99)',
      fogGroundBlend: 0.38,
      horizonFogBlend: 0.42,
      skyHorizonBlend: 0.68,
      atmosphereBlend: 0.58,
    },
    lowOblique: {
      skyColor: '#0d1f3f',
      horizonColor: '#45658e',
      fogColor: 'rgb(48, 67, 99)',
      fogGroundBlend: 0.38,
      horizonFogBlend: 0.42,
      skyHorizonBlend: 0.68,
      atmosphereBlend: 0.58,
    },
    cinematicBalanced: {
      skyColor: '#060c1c', // azul noche profunda
      horizonColor: '#18284a', // azul oscuro en el horizonte
      fogColor: 'rgb(16, 26, 52)', // neblina azul-índigo oscura, sin blanquear
      fogGroundBlend: 0.42,
      horizonFogBlend: 0.24,
      skyHorizonBlend: 0.48,
      atmosphereBlend: 0.2, // bajo — montaña visible y natural, apenas velada en azul
    },
    deepHorizon: {
      skyColor: '#0d1f3f',
      horizonColor: '#45658e',
      fogColor: 'rgb(48, 67, 99)',
      fogGroundBlend: 0.38,
      horizonFogBlend: 0.42,
      skyHorizonBlend: 0.68,
      atmosphereBlend: 0.58,
    },
    territorialWide: {
      skyColor: '#0d1f3f',
      horizonColor: '#45658e',
      fogColor: 'rgb(48, 67, 99)',
      fogGroundBlend: 0.38,
      horizonFogBlend: 0.42,
      skyHorizonBlend: 0.68,
      atmosphereBlend: 0.58,
    },
  };

// ─── Entry view registry (provisional) ───────────────────────────────────────

const ENTRY_VIEW_REGISTRY: Record<string, PersistedView> = {
  // IRPHa San Juan — posición calibrada visualmente 2026-05-25.
  'cbfe922d-95d6-4e61-ae73-4ffd67af06b2': {
    center: [-68.61203, -31.51659],
    zoom: 12.42,
    pitch: 74.5,
    bearing: -36.1,
  },
};

function getProjectEntryView(projectId: string | null): PersistedView | null {
  if (!projectId) return null;
  return ENTRY_VIEW_REGISTRY[projectId] ?? null;
}

function resolveActiveTerritorialPreset(): TerritorialCameraPreset {
  return TERRITORIAL_CAMERA_PRESETS[TERRITORIAL_CAMERA_PRESET];
}

function resolveActiveSatelliteAtmospherePreset(): SatelliteAtmospherePreset {
  return SATELLITE_ATMOSPHERE_PRESETS[TERRITORIAL_CAMERA_PRESET];
}

function applyTerritorialCameraPreset(baseCamera: TerritorialCamera): TerritorialCamera {
  const preset = resolveActiveTerritorialPreset();
  return {
    ...baseCamera,
    center: [
      baseCamera.center[0] + preset.centerOffset[0],
      baseCamera.center[1] + preset.centerOffset[1],
    ],
    zoom: baseCamera.zoom + preset.zoomDelta,
    pitch: preset.pitch,
    bearing: baseCamera.bearing + preset.bearingDelta,
  };
}

// ─── Cover mode — cámara y atmósfera ─────────────────────────────────────────
// Pitch 61° (vs 74.5° del satellite) — menos horizonte, tiles más cercanos,
// mayor definición percibida del raster Esri.
// terrainExaggeration 1.08 — relieve presente sin distorsión exagerada.
const COVER_CAMERA_PRESET: TerritorialCameraPreset = {
  centerOffset: [-0.004, 0.008],
  zoomDelta: -0.25,
  pitch: 61,
  bearingDelta: -14,
  maxPitch: 70,
  terrainExaggeration: 1.08,
};

const COVER_ATMOSPHERE: SatelliteAtmospherePreset = {
  skyColor: '#080e1c',
  horizonColor: '#141e30',
  fogColor: 'rgb(10, 16, 28)',
  fogGroundBlend: 0.12,
  horizonFogBlend: 0.1,
  skyHorizonBlend: 0.28,
  atmosphereBlend: 0.08,
};

function applyCoverCameraPreset(baseCamera: TerritorialCamera): TerritorialCamera {
  return {
    ...baseCamera,
    center: [
      baseCamera.center[0] + COVER_CAMERA_PRESET.centerOffset[0],
      baseCamera.center[1] + COVER_CAMERA_PRESET.centerOffset[1],
    ],
    zoom: baseCamera.zoom + COVER_CAMERA_PRESET.zoomDelta,
    pitch: COVER_CAMERA_PRESET.pitch,
    bearing: baseCamera.bearing + COVER_CAMERA_PRESET.bearingDelta,
  };
}

// Pitch de arranque — usado en el constructor del mapa antes de que lleguen los datos.
// Debe ser cercano al pitch final de cada modo para evitar la transición plano→oblicuo.
function resolveInitialPitch(mode: SceneMode): number {
  if (mode === 'satellite') return resolveActiveTerritorialPreset().pitch;
  if (mode === 'cover') return COVER_CAMERA_PRESET.pitch;
  return 55; // dark / light / topographic: perspectiva moderada visible desde el inicio
}

function resolveTerrainExaggeration(mode: SceneMode): number {
  if (mode === 'satellite') return resolveActiveTerritorialPreset().terrainExaggeration;
  if (mode === 'cover') return COVER_CAMERA_PRESET.terrainExaggeration;
  return 1.0;
}

// ─── Style resolver ───────────────────────────────────────────────────────────

function resolveBaseStyle(mode: SceneMode): string | maplibregl.StyleSpecification {
  if (mode === 'cover') return ESRI_COVER_STYLE;
  if (mode === 'topographic') return mapStyleService.getStyle('terrain') as string;
  if (mode === 'satellite') return mapStyleService.getStyle('satellite') as string;
  return mapStyleService.getStyle(mode as Parameters<typeof mapStyleService.getStyle>[0]) as string;
}

// ─── Line style expressions — scope-aware (dark mode) ─────────────────────────
// inside: protagonismo completo por roadClass. outside: atenuado, continuo.
// Halo: solo en inside; width 0 en outside y en residential.

const LINE_COLOR: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'match',
    ['get', 'roadClass'],
    'primary',
    '#8ecfda',
    'secondary',
    '#7abfcc',
    'tertiary',
    '#66aeba',
    'residential',
    '#4f96a3',
    '#3d8290',
  ],
  '#4a8090',
];
const LINE_WIDTH: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'match',
    ['get', 'roadClass'],
    'primary',
    3.5,
    'secondary',
    2.8,
    'tertiary',
    2.0,
    'residential',
    1.2,
    0.8,
  ],
  0.7,
];
const LINE_OPACITY: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'match',
    ['get', 'roadClass'],
    'primary',
    0.95,
    'secondary',
    0.9,
    'tertiary',
    0.75,
    'residential',
    0.55,
    0.3,
  ],
  0.25,
];
const LINE_WIDTH_HALO: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  ['match', ['get', 'roadClass'], 'primary', 7, 'secondary', 5, 'tertiary', 3.5, 0],
  0,
];
const LINE_OPACITY_HALO: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  ['match', ['get', 'roadClass'], 'primary', 0.15, 'secondary', 0.12, 'tertiary', 0.09, 0],
  0,
];

// ─── Line style expressions — satellite syntax-driven palette ────────────────
// Iluminación por Estructura de Sintaxis Espacial (syntaxStructureScore ∈ [0,1]).
// syntaxStructureScore: percentil combinado = 50% NAIN global + 50% NAIN r=1200m.
// Replica perfil 'equilibrado' de NetworkMorphologicalHierarchyService.
// SYNTAX_STRUCTURE_FALLBACK (0.10): sin dato → subordinado pero presente.
// Scope 'outside': plano tenue sin distinción sintáctica.
//
// Rampa visual intencional (portada refinada):
//   score 0.00 → opacity 0.10, width 0.35  (cul-de-sacs, casi invisibles)
//   score 0.30 → opacity 0.18, width 0.65  (red menor, muy subordinada)
//   score 0.55 → opacity 0.35, width 1.10  (red media, acompañante)
//   score 0.75 → opacity 0.65, width 1.80  (red principal, visible)
//   score 1.00 → opacity 0.90, width 3.00  (corredores estructurales, dominantes)
//
// Halo activo solo desde score ≥ 0.50 — evita nube luminosa en el tejido menor.
// Glow tight activo solo desde score ≥ 0.65 — concentrado en estructurales.

const LINE_COLOR_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    '#a86e24', // bajo: ámbar-marrón visible
    0.3,
    '#c08828',
    0.6,
    '#d8a430',
    0.85,
    '#eec040',
    1.0,
    '#f8e070', // alto: amarillo-dorado brillante
  ],
  '#7a4a10', // fuera: marrón tenue
];
const LINE_WIDTH_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    0.9,
    0.3,
    1.3,
    0.55,
    1.7,
    0.75,
    2.4,
    1.0,
    3.5,
  ],
  0.6,
];
const LINE_OPACITY_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    0.44,
    0.3,
    0.56,
    0.55,
    0.68,
    0.75,
    0.82,
    1.0,
    0.94,
  ],
  0.28,
];
const LINE_WIDTH_HALO_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    0,
    0.5,
    1.5,
    0.75,
    3.5,
    1.0,
    6.0,
  ],
  0,
];
const LINE_OPACITY_HALO_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    0,
    0.5,
    0.04,
    0.75,
    0.1,
    1.0,
    0.18,
  ],
  0,
];
const LINE_WIDTH_GLOW_TIGHT_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    0,
    0.65,
    1.5,
    0.85,
    3.5,
    1.0,
    6.0,
  ],
  0,
];
const LINE_OPACITY_GLOW_TIGHT_SAT: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    0,
    0.65,
    0.03,
    0.85,
    0.08,
    1.0,
    0.14,
  ],
  0,
];

// ─── Node style expressions — satellite syntax-driven ────────────────────────
// nodeSyntaxStructureScore: máximo syntaxStructureScore de los segmentos incidentes.
// Decisión: máximo → resalta intersecciones que conectan corredores estructurales.
// Sin OSM roadClass/highway en ningún parámetro de nodo.
//
// Escala muy contenida — nodos discretos, sin dominar la imagen.
// Radios máximos: base 3.2, glow-up 3.0, core 1.4 (vs. 7/12/2.6 anteriores).

const NODE_GLOW_BASE_RADIUS_SAT: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'nodeSyntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
  0.0,
  1.2,
  0.35,
  1.8,
  0.65,
  2.4,
  1.0,
  3.2,
];
const NODE_GLOW_BASE_OPACITY_SAT: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'nodeSyntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
  0.0,
  0.01,
  0.35,
  0.02,
  0.65,
  0.04,
  1.0,
  0.07,
];
const NODE_GLOW_UP_RADIUS_SAT: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'nodeSyntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
  0.0,
  0,
  0.5,
  1.8,
  0.75,
  2.4,
  1.0,
  3.0,
];
const NODE_GLOW_UP_OPACITY_SAT: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'nodeSyntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
  0.0,
  0,
  0.5,
  0.05,
  0.75,
  0.09,
  1.0,
  0.14,
];
const NODE_CORE_RADIUS_SAT: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'nodeSyntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
  0.0,
  0.6,
  0.35,
  0.8,
  0.65,
  1.0,
  1.0,
  1.4,
];
const NODE_CORE_OPACITY_SAT: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'nodeSyntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
  0.0,
  0.12,
  0.35,
  0.25,
  0.65,
  0.45,
  1.0,
  0.65,
];

// ─── Network palette resolver ─────────────────────────────────────────────────

interface NetworkPalette {
  lineColor: maplibregl.ExpressionSpecification | string;
  lineWidth: maplibregl.ExpressionSpecification;
  lineOpacity: maplibregl.ExpressionSpecification;
  lineWidthHalo: maplibregl.ExpressionSpecification;
  lineOpacityHalo: maplibregl.ExpressionSpecification;
  glowColor: string | null;
  glowWidth: maplibregl.ExpressionSpecification | null;
  glowOpacity: maplibregl.ExpressionSpecification | null;
  nodeGlowBaseColor: string | null;
  nodeGlowBaseRadius: maplibregl.ExpressionSpecification | null;
  nodeGlowBaseOpacity: maplibregl.ExpressionSpecification | null;
  nodeGlowUpColor: string | null;
  nodeGlowUpRadius: maplibregl.ExpressionSpecification | null;
  nodeGlowUpOpacity: maplibregl.ExpressionSpecification | null;
  nodeCoreColor: string | null;
  nodeCoreRadius: maplibregl.ExpressionSpecification | null;
  nodeCoreOpacity: maplibregl.ExpressionSpecification | null;
  haloColor: string;
}

/**
 * Genera expresión MapLibre de valor constante — útil para modo diagnóstico.
 * `['interpolate', ['linear'], ['zoom'], 0, v, 24, v]` siempre devuelve v.
 */
function constExpr(v: number): maplibregl.ExpressionSpecification {
  return ['interpolate', ['linear'], ['zoom'], 0, v, 24, v];
}

// Rampa dorada para cover — hue 38–44°, tope #c8941c (oro), no amarillo.
const LINE_COLOR_COVER: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'scope'], 'inside'],
  [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'syntaxStructureScore'], SYNTAX_STRUCTURE_FALLBACK],
    0.0,
    '#6e3c08',
    0.3,
    '#8c5410',
    0.6,
    '#a87018',
    0.85,
    '#bc8c1e',
    1.0,
    '#c8941c',
  ],
  '#4a2806',
];

function resolveNetworkPalette(mode: SceneMode): NetworkPalette {
  if (mode === 'cover') {
    return {
      lineColor: LINE_COLOR_COVER,
      lineWidth: LINE_WIDTH_SAT,
      lineOpacity: LINE_OPACITY_SAT,
      lineWidthHalo: LINE_WIDTH_HALO_SAT,
      lineOpacityHalo: LINE_OPACITY_HALO_SAT,
      // Sin glow tight — sobre fondo diurno Esri el glow claro arrastra el color
      // hacia amarillo. La línea dorada se lee mejor sin interferencia luminosa.
      glowColor: null,
      glowWidth: null,
      glowOpacity: null,
      nodeGlowBaseColor: '#c8901a',
      nodeGlowBaseRadius: NODE_GLOW_BASE_RADIUS_SAT,
      nodeGlowBaseOpacity: NODE_GLOW_BASE_OPACITY_SAT,
      nodeGlowUpColor: '#d4a828',
      nodeGlowUpRadius: NODE_GLOW_UP_RADIUS_SAT,
      nodeGlowUpOpacity: NODE_GLOW_UP_OPACITY_SAT,
      nodeCoreColor: '#d4a020',
      nodeCoreRadius: NODE_CORE_RADIUS_SAT,
      nodeCoreOpacity: NODE_CORE_OPACITY_SAT,
      haloColor: 'rgba(0,0,0,0.90)',
    };
  }
  if (mode === 'satellite') {
    if (FORCE_TERRITORIAL_NETWORK_VISIBLE) {
      // Modo emergencia visual — red plana muy visible, sin jerarquía sintáctica.
      // Activar para confirmar que layers/orden están bien antes de ajustar rampa.
      return {
        lineColor: '#7fffff',
        lineWidth: constExpr(2.2),
        lineOpacity: constExpr(0.92),
        lineWidthHalo: constExpr(6.0),
        lineOpacityHalo: constExpr(0.4),
        glowColor: '#ffffff',
        glowWidth: constExpr(4.0),
        glowOpacity: constExpr(0.22),
        nodeGlowBaseColor: '#ffffff',
        nodeGlowBaseRadius: constExpr(5.0),
        nodeGlowBaseOpacity: constExpr(0.2),
        nodeGlowUpColor: '#ffffff',
        nodeGlowUpRadius: constExpr(10.0),
        nodeGlowUpOpacity: constExpr(0.15),
        nodeCoreColor: '#ffffff',
        nodeCoreRadius: constExpr(2.8),
        nodeCoreOpacity: constExpr(0.9),
        haloColor: 'rgba(0,0,0,0.85)',
      };
    }
    return {
      lineColor: LINE_COLOR_SAT,
      lineWidth: LINE_WIDTH_SAT,
      lineOpacity: LINE_OPACITY_SAT,
      lineWidthHalo: LINE_WIDTH_HALO_SAT,
      lineOpacityHalo: LINE_OPACITY_HALO_SAT,
      glowColor: '#c49020',
      glowWidth: LINE_WIDTH_GLOW_TIGHT_SAT,
      glowOpacity: LINE_OPACITY_GLOW_TIGHT_SAT,
      nodeGlowBaseColor: '#ecd4a2',
      nodeGlowBaseRadius: NODE_GLOW_BASE_RADIUS_SAT,
      nodeGlowBaseOpacity: NODE_GLOW_BASE_OPACITY_SAT,
      nodeGlowUpColor: '#f6e4bf',
      nodeGlowUpRadius: NODE_GLOW_UP_RADIUS_SAT,
      nodeGlowUpOpacity: NODE_GLOW_UP_OPACITY_SAT,
      nodeCoreColor: '#f5e0b9',
      nodeCoreRadius: NODE_CORE_RADIUS_SAT,
      nodeCoreOpacity: NODE_CORE_OPACITY_SAT,
      haloColor: 'rgba(0,0,0,0.85)', // negro — separa línea del basemap fotográfico
    };
  }
  return {
    lineColor: LINE_COLOR,
    lineWidth: LINE_WIDTH,
    lineOpacity: LINE_OPACITY,
    lineWidthHalo: LINE_WIDTH_HALO,
    lineOpacityHalo: LINE_OPACITY_HALO,
    glowColor: null,
    glowWidth: null,
    glowOpacity: null,
    nodeGlowBaseColor: null,
    nodeGlowBaseRadius: null,
    nodeGlowBaseOpacity: null,
    nodeGlowUpColor: null,
    nodeGlowUpRadius: null,
    nodeGlowUpOpacity: null,
    nodeCoreColor: null,
    nodeCoreRadius: null,
    nodeCoreOpacity: null,
    haloColor: 'rgba(0,0,0,0.85)',
  };
}

// ─── Satellite nodes ──────────────────────────────────────────────────────────
// Jerarquía visual de nodos basada en Estructura de Sintaxis Espacial, NO en OSM.
// nodeSyntaxStructureScore = máximo syntaxStructureScore de segmentos incidentes.
// Decisión de diseño: máximo → resalta intersecciones que conectan corredores
// estructurales importantes aunque solo uno de sus lados tenga score alto.

interface SatelliteNodeAccumulator {
  maxSyntaxStructureScore: number;
  degree: number;
  sumLng: number;
  sumLat: number;
  sampleCount: number;
}

function getGeometryEndpoints(geometry: LineString | MultiLineString): Position[] {
  if (geometry.type === 'LineString') {
    if (geometry.coordinates.length < 2) return [];
    return [geometry.coordinates[0], geometry.coordinates[geometry.coordinates.length - 1]];
  }
  const endpoints: Position[] = [];
  for (const segment of geometry.coordinates) {
    if (segment.length < 2) continue;
    endpoints.push(segment[0], segment[segment.length - 1]);
  }
  return endpoints;
}

/**
 * Deriva nodos de intersección a partir de endpoints de segmentos.
 * nodeSyntaxStructureScore = máximo de los scores de segmentos incidentes.
 * Requiere que los features ya tengan syntaxStructureScore en sus properties.
 */
function deriveSatelliteNodes(data: FeatureCollection): FeatureCollection<Point> {
  const buckets = new Map<string, SatelliteNodeAccumulator>();

  function addCoordinate(coord: Position, edgeScore: number): void {
    const lng = coord[0];
    const lat = coord[1];
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const key = `${lng.toFixed(NODE_COORD_PRECISION)}:${lat.toFixed(NODE_COORD_PRECISION)}`;
    const bucket = buckets.get(key) ?? {
      maxSyntaxStructureScore: 0,
      degree: 0,
      sumLng: 0,
      sumLat: 0,
      sampleCount: 0,
    };
    bucket.degree += 1;
    bucket.sumLng += lng;
    bucket.sumLat += lat;
    bucket.sampleCount += 1;
    if (edgeScore > bucket.maxSyntaxStructureScore) {
      bucket.maxSyntaxStructureScore = edgeScore;
    }
    buckets.set(key, bucket);
  }

  for (const feature of data.features) {
    if (!feature.geometry) continue;
    if (feature.geometry.type !== 'LineString' && feature.geometry.type !== 'MultiLineString')
      continue;
    const edgeScore =
      typeof feature.properties?.['syntaxStructureScore'] === 'number'
        ? feature.properties['syntaxStructureScore']
        : SYNTAX_STRUCTURE_FALLBACK;
    const endpoints = getGeometryEndpoints(feature.geometry);
    for (const endpoint of endpoints) addCoordinate(endpoint, edgeScore);
  }

  const features = Array.from(buckets.values())
    .map(bucket => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [bucket.sumLng / bucket.sampleCount, bucket.sumLat / bucket.sampleCount],
      },
      properties: {
        nodeSyntaxStructureScore: Number(bucket.maxSyntaxStructureScore.toFixed(4)),
        degree: bucket.degree,
      },
    }))
    .sort((a, b) => b.properties.nodeSyntaxStructureScore - a.properties.nodeSyntaxStructureScore);

  return {
    type: 'FeatureCollection',
    features,
  };
}

// ─── Satellite atmospheric adjustments ───────────────────────────────────────

/**
 * Aplica atmósfera blue-hour SOLO en modo satellite.
 * Debe llamarse después de style.load.
 * No-fatal — cada operación falla silenciosamente de forma independiente.
 *
 *  1. Raster adjustment: desatura y oscurece el basemap → territorio como contexto.
 *  2. Sky/atmosphere: blue-hour nocturna vía setSky.
 *  3. Atmospheric overlay: velo azul-gris entre basemap y red.
 *  4. Label suppression: elimina etiquetas del hybrid → escena más limpia y premium.
 */
// eslint-disable-next-line complexity
function applyAtmosphericSatellite(map: maplibregl.Map): void {
  const styleLayers = map.getStyle()?.layers ?? [];
  const atmosphere = resolveActiveSatelliteAtmospherePreset();
  const roadKeywords = [
    'road',
    'roads',
    'street',
    'highway',
    'transport',
    'transportation',
    'bridge',
    'tunnel',
    'route',
  ];

  // 1. Raster adjustment — satélite vivo, apenas suavizado.
  // hue-rotate: 0 — sin rotación de color, el hybrid ya tiene tonos correctos.
  // saturation: -0.08 — baja apenas el cromatismo para dejar respirar la red cálida.
  // brightness-max: 0.76 — oscurecimiento moderado, mantiene legibilidad territorial.
  // El tono blue-hour proviene de atmósfera/overlays, no de destruir el color base.
  for (const layer of styleLayers) {
    if (layer.type !== 'raster') continue;
    try {
      map.setPaintProperty(layer.id, 'raster-saturation', 0.08); // viveza moderada
      map.setPaintProperty(layer.id, 'raster-brightness-min', 0.02);
      map.setPaintProperty(layer.id, 'raster-brightness-max', 0.72);
      map.setPaintProperty(layer.id, 'raster-contrast', 0.1);
      map.setPaintProperty(layer.id, 'raster-hue-rotate', -8); // giro rojo: saca los ocres/terracota del desierto
    } catch (_: unknown) {
      // non-fatal — la capa raster puede no aceptar todos los props
    }
  }

  // 2. Sky / atmosphere — blue-hour via setSky (API MapLibre 4.7.1).
  // Ajuste mínimo para abrir una franja superior de cielo/horizonte sin lavar el suelo.
  try {
    map.setSky({
      'sky-color': atmosphere.skyColor,
      'horizon-color': atmosphere.horizonColor,
      'fog-color': atmosphere.fogColor,
      'fog-ground-blend': atmosphere.fogGroundBlend,
      'horizon-fog-blend': atmosphere.horizonFogBlend,
      'sky-horizon-blend': atmosphere.skyHorizonBlend,
      'atmosphere-blend': atmosphere.atmosphereBlend,
    });
  } catch (_: unknown) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[ts:territory:sat] setSky not supported or style not loaded');
    }
  }

  // 3. Atmospheric overlay — velo azul-gris entre basemap y capas de red.
  // addLayer sin beforeId → se inserta en el top del stack actual (encima del raster,
  // debajo de los layers de red que aún no han sido añadidos).
  if (!map.getLayer(LAYER_SAT_ATMOSPHERE_ID)) {
    try {
      map.addLayer({
        id: LAYER_SAT_ATMOSPHERE_ID,
        type: 'background',
        paint: { 'background-color': 'rgba(12, 20, 40, 0.08)' },
      });
    } catch (_: unknown) {
      // non-fatal
    }
  }

  // 4. Label suppression — hybrid trae etiquetas que compiten con la red
  for (const layer of styleLayers) {
    if (layer.type !== 'symbol') continue;
    try {
      map.setLayoutProperty(layer.id, 'visibility', 'none');
    } catch (_: unknown) {
      // non-fatal
    }
  }

  // 5. Basemap road suppression — oculta vialidad fina del estilo hybrid
  // sin tocar la red canónica territorial ni otras capas propias.
  for (const layer of styleLayers) {
    if (layer.type !== 'line') continue;
    if (layer.id.startsWith('territorial-network-') || layer.id.startsWith('territorial-')) {
      continue;
    }

    const layerId = layer.id.toLowerCase();
    const sourceLayer =
      typeof layer['source-layer'] === 'string' ? layer['source-layer'].toLowerCase() : '';
    const isRoadLike = roadKeywords.some(
      keyword => layerId.includes(keyword) || sourceLayer.includes(keyword)
    );
    if (!isRoadLike) continue;

    try {
      map.setLayoutProperty(layer.id, 'visibility', 'none');
    } catch (_: unknown) {
      // non-fatal
    }
  }
}

// ─── Cover atmosphere ─────────────────────────────────────────────────────────

/**
 * Ajustes raster y atmósfera para modo cover (Esri World Imagery).
 * Contrario a applyAtmosphericSatellite: sin oscurecimiento, sin label suppression
 * (el estilo Esri inline no tiene layers vectoriales), atmósfera muy sutil.
 */
function applyCoverAtmosphere(map: maplibregl.Map): void {
  const styleLayers = map.getStyle()?.layers ?? [];

  // Raster — imagen natural, mínimo procesamiento para máxima definición.
  for (const layer of styleLayers) {
    if (layer.type !== 'raster') continue;
    try {
      map.setPaintProperty(layer.id, 'raster-saturation', 0.05);
      map.setPaintProperty(layer.id, 'raster-brightness-min', 0.0);
      map.setPaintProperty(layer.id, 'raster-brightness-max', 0.94);
      map.setPaintProperty(layer.id, 'raster-contrast', 0.08);
      map.setPaintProperty(layer.id, 'raster-hue-rotate', 0);
    } catch (_: unknown) {
      /* non-fatal */
    }
  }

  // Sky — atmósfera nocturna muy sutil para no blanquear el horizonte.
  try {
    map.setSky({
      'sky-color': COVER_ATMOSPHERE.skyColor,
      'horizon-color': COVER_ATMOSPHERE.horizonColor,
      'fog-color': COVER_ATMOSPHERE.fogColor,
      'fog-ground-blend': COVER_ATMOSPHERE.fogGroundBlend,
      'horizon-fog-blend': COVER_ATMOSPHERE.horizonFogBlend,
      'sky-horizon-blend': COVER_ATMOSPHERE.skyHorizonBlend,
      'atmosphere-blend': COVER_ATMOSPHERE.atmosphereBlend,
    });
  } catch (_: unknown) {
    /* non-fatal */
  }

  // Velo muy sutil cálido-oscuro — separa basemap Esri de la red dorada.
  if (!map.getLayer(LAYER_SAT_ATMOSPHERE_ID)) {
    try {
      map.addLayer({
        id: LAYER_SAT_ATMOSPHERE_ID,
        type: 'background',
        paint: { 'background-color': 'rgba(10, 6, 2, 0.06)' },
      });
    } catch (_: unknown) {
      /* non-fatal */
    }
  }
}

// ─── Terrain ──────────────────────────────────────────────────────────────────

/**
 * Agrega dos sources raster-dem independientes (terrain + hillshade) y activa terrain 3D.
 * Sources separados eliminan la advertencia de MapLibre sobre mismo source.
 * Debe llamarse ANTES de addDataLayers para que hillshade quede debajo de la red.
 * Falla de forma silenciosa — no rompe la escena.
 */
function ensureTerrain(map: maplibregl.Map, exaggeration: number): void {
  if (!MAPTILER_DEM_KEY) return;
  if (!map.isStyleLoaded()) {
    void map.once('style.load', () => {
      ensureTerrain(map, exaggeration);
    });
    return;
  }
  const demUrl = `https://api.maptiler.com/tiles/terrain-rgb-v2/tiles.json?key=${MAPTILER_DEM_KEY}`;
  const demSpec = {
    type: 'raster-dem' as const,
    url: demUrl,
    tileSize: 256,
    encoding: 'mapbox' as const,
  };
  try {
    if (!map.getSource(TERRAIN_SOURCE_ID)) map.addSource(TERRAIN_SOURCE_ID, demSpec);
    if (!map.getSource(HILLSHADE_SOURCE_ID)) map.addSource(HILLSHADE_SOURCE_ID, demSpec);
    if (!map.getLayer(LAYER_HILLSHADE_ID)) {
      // beforeId = undefined → hillshade al top actual; red se agrega después y queda encima.
      map.addLayer({
        id: LAYER_HILLSHADE_ID,
        type: 'hillshade',
        source: HILLSHADE_SOURCE_ID,
        paint: {
          'hillshade-illumination-direction': 315,
          'hillshade-exaggeration': 0.75,
          'hillshade-shadow-color': 'rgba(0, 0, 0, 0.65)',
          'hillshade-highlight-color': 'rgba(255, 255, 255, 0.35)',
        },
      });
    }
    map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration });
  } catch (_: unknown) {
    // Terrain failure es no-fatal — mapa continúa sin relieve.
  }
}

function applyPitchConstraints(map: maplibregl.Map, mode: SceneMode): void {
  const preset = resolveActiveTerritorialPreset();
  if (mode === 'satellite') map.setMaxPitch(preset.maxPitch);
  else if (mode === 'cover') map.setMaxPitch(COVER_CAMERA_PRESET.maxPitch);
  else map.setMaxPitch(DEFAULT_MAX_PITCH);
}

// ─── Idle camera motion ───────────────────────────────────────────────────────

interface IdleHandle {
  stop: () => void;
}

const IDLE_MOTION = {
  startDelayMs: 1800,
  stepDurationMs: 13000,
  bearingStepDeg: 1.2,
  maxBearingDriftDeg: 0,
  zoomStep: 0,
  maxZoomDrift: 0,
} as const;

function startContinuousIdleMotion(map: maplibregl.Map, baseCamera: TerritorialCamera): IdleHandle {
  let activeTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let currentStep = 0;
  const maxSteps = Math.floor(IDLE_MOTION.maxBearingDriftDeg / IDLE_MOTION.bearingStepDeg);

  function advance(): void {
    if (stopped || currentStep >= maxSteps) return;
    currentStep += 1;
    const bearing = baseCamera.bearing + currentStep * IDLE_MOTION.bearingStepDeg;
    const zoom =
      baseCamera.zoom + Math.min(currentStep * IDLE_MOTION.zoomStep, IDLE_MOTION.maxZoomDrift);
    map.easeTo({
      bearing,
      zoom,
      pitch: baseCamera.pitch,
      duration: IDLE_MOTION.stepDurationMs,
      easing: (t: number): number => t,
    });
    if (currentStep < maxSteps) {
      activeTimer = setTimeout((): void => {
        advance();
      }, IDLE_MOTION.stepDurationMs);
    }
  }

  activeTimer = setTimeout((): void => {
    advance();
  }, IDLE_MOTION.startDelayMs);
  return {
    stop(): void {
      stopped = true;
      if (activeTimer !== null) clearTimeout(activeTimer);
      map.stop();
    },
  };
}

// ─── Layer registration ───────────────────────────────────────────────────────

// eslint-disable-next-line complexity, max-lines-per-function
function addDataLayers(
  map: maplibregl.Map,
  data: FeatureCollection,
  palette: NetworkPalette
): void {
  const nodeData = palette.nodeCoreColor ? deriveSatelliteNodes(data) : null;
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(data);
  }
  if (nodeData) {
    if (!map.getSource(SOURCE_NODES_ID)) {
      map.addSource(SOURCE_NODES_ID, { type: 'geojson', data: nodeData });
    } else {
      (map.getSource(SOURCE_NODES_ID) as maplibregl.GeoJSONSource).setData(nodeData);
    }
  }
  if (!map.getLayer(LAYER_HALO_ID)) {
    map.addLayer({
      id: LAYER_HALO_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      // Color por paleta; width/opacity scope-aware; opacity=0 hasta fade-in.
      paint: {
        'line-color': palette.haloColor,
        'line-width': palette.lineWidthHalo,
        'line-opacity': 0,
      },
    });
  }
  if (
    palette.glowColor &&
    palette.glowWidth &&
    palette.glowOpacity &&
    !map.getLayer(LAYER_GLOW_TIGHT_ID)
  ) {
    map.addLayer({
      id: LAYER_GLOW_TIGHT_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': palette.glowColor,
        'line-width': palette.glowWidth,
        'line-opacity': 0,
      },
    });
  }
  if (!map.getLayer(LAYER_ID)) {
    map.addLayer({
      id: LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      // Color y width scope+roadClass desde el inicio; opacity=0 hasta fade-in.
      paint: {
        'line-color': palette.lineColor,
        'line-width': palette.lineWidth,
        'line-opacity': 0,
      },
    });
  }
  if (
    nodeData &&
    palette.nodeGlowBaseColor &&
    palette.nodeGlowBaseRadius &&
    palette.nodeGlowBaseOpacity &&
    !map.getLayer(LAYER_NODE_GLOW_BASE_ID)
  ) {
    map.addLayer({
      id: LAYER_NODE_GLOW_BASE_ID,
      type: 'circle',
      source: SOURCE_NODES_ID,
      paint: {
        'circle-color': palette.nodeGlowBaseColor,
        'circle-radius': palette.nodeGlowBaseRadius,
        'circle-opacity': 0,
        'circle-blur': 0.68,
      },
    });
  }
  if (
    nodeData &&
    palette.nodeGlowUpColor &&
    palette.nodeGlowUpRadius &&
    palette.nodeGlowUpOpacity &&
    !map.getLayer(LAYER_NODE_GLOW_UP_ID)
  ) {
    map.addLayer({
      id: LAYER_NODE_GLOW_UP_ID,
      type: 'circle',
      source: SOURCE_NODES_ID,
      paint: {
        'circle-color': palette.nodeGlowUpColor,
        'circle-radius': palette.nodeGlowUpRadius,
        'circle-opacity': 0,
        'circle-blur': 0.9,
        'circle-translate': [0, -9],
        'circle-translate-anchor': 'viewport',
      },
    });
  }
  if (
    nodeData &&
    palette.nodeCoreColor &&
    palette.nodeCoreRadius &&
    palette.nodeCoreOpacity &&
    !map.getLayer(LAYER_NODE_CORE_ID)
  ) {
    map.addLayer({
      id: LAYER_NODE_CORE_ID,
      type: 'circle',
      source: SOURCE_NODES_ID,
      paint: {
        'circle-color': palette.nodeCoreColor,
        'circle-radius': palette.nodeCoreRadius,
        'circle-opacity': 0,
        'circle-stroke-width': 0,
      },
    });
  }
  if (import.meta.env.DEV) devLogAfterAdd(map, Date.now(), data.features.length);
  if (DEBUG_TERRITORIAL_NETWORK_VISIBILITY && import.meta.env.DEV)
    devLogNetworkVisibility(map, data);
}

/**
 * Anima line-opacity de 0 a target en FADE_IN_MS vía rAF.
 *
 * startTime se captura en el PRIMER frame (no antes) para evitar que
 * performance.now() quede delante del timestamp rAF y produzca t negativo.
 * MapLibre lanza errores de validación como eventos (no excepciones), por lo
 * que el clamping en [0,1] es obligatorio — el try-catch solo cubre map removed.
 */
/**
 * Anima line-opacity de LAYER_ID de 0 a TARGET_OPACITY_LINES en FADE_IN_MS.
 * El halo se maneja por separado (snap a expresión en withFadeIn).
 * onComplete se llama al finalizar — usa para setear la expresión final.
 */
function fadeInLayers(map: maplibregl.Map, onComplete?: () => void): void {
  let startTime: number | null = null;
  function tick(now: number): void {
    try {
      if (startTime === null) startTime = now;
      const progress = Math.min(1, Math.max(0, (now - startTime) / FADE_IN_MS));
      const eased = 1 - Math.pow(1 - progress, 2); // ease-out quad, siempre en [0,1]
      if (!map.getLayer(LAYER_ID)) return;
      map.setPaintProperty(LAYER_ID, 'line-opacity', TARGET_OPACITY_LINES * eased);
      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }
      onComplete?.();
    } catch (_: unknown) {
      return; // mapa removido — abortar animación
    }
  }
  requestAnimationFrame(tick);
}

/**
 * Aplica valores literales de paint para modo de prueba FORCE_TERRITORIAL_NETWORK_VISIBLE.
 * Bypasa completamente el sistema de expresiones y el fade-in.
 * Todos los valores son escalares — sin sintaxScore, sin coalesce, sin rampa.
 */
function applyForcePaint(map: maplibregl.Map): void {
  if (map.getLayer(LAYER_HALO_ID)) {
    map.setPaintProperty(LAYER_HALO_ID, 'line-color', 'rgba(255,255,255,0.85)');
    map.setPaintProperty(LAYER_HALO_ID, 'line-opacity', 0.65);
    map.setPaintProperty(LAYER_HALO_ID, 'line-width', 5);
    map.setPaintProperty(LAYER_HALO_ID, 'line-blur', 2);
  }
  if (map.getLayer(LAYER_GLOW_TIGHT_ID)) {
    map.setPaintProperty(LAYER_GLOW_TIGHT_ID, 'line-color', '#7fffff');
    map.setPaintProperty(LAYER_GLOW_TIGHT_ID, 'line-opacity', 0.95);
    map.setPaintProperty(LAYER_GLOW_TIGHT_ID, 'line-width', 3);
    map.setPaintProperty(LAYER_GLOW_TIGHT_ID, 'line-blur', 1);
  }
  if (map.getLayer(LAYER_ID)) {
    map.setPaintProperty(LAYER_ID, 'line-color', '#eaffff');
    map.setPaintProperty(LAYER_ID, 'line-opacity', 1.0);
    map.setPaintProperty(LAYER_ID, 'line-width', 2);
  }
  if (map.getLayer(LAYER_NODE_GLOW_BASE_ID)) {
    map.setPaintProperty(LAYER_NODE_GLOW_BASE_ID, 'circle-color', '#ffe2a3');
    map.setPaintProperty(LAYER_NODE_GLOW_BASE_ID, 'circle-opacity', 0.75);
    map.setPaintProperty(LAYER_NODE_GLOW_BASE_ID, 'circle-radius', 5);
    map.setPaintProperty(LAYER_NODE_GLOW_BASE_ID, 'circle-blur', 0.6);
  }
  if (map.getLayer(LAYER_NODE_GLOW_UP_ID)) {
    map.setPaintProperty(LAYER_NODE_GLOW_UP_ID, 'circle-color', '#fff1c8');
    map.setPaintProperty(LAYER_NODE_GLOW_UP_ID, 'circle-opacity', 0.8);
    map.setPaintProperty(LAYER_NODE_GLOW_UP_ID, 'circle-radius', 3);
  }
  if (map.getLayer(LAYER_NODE_CORE_ID)) {
    map.setPaintProperty(LAYER_NODE_CORE_ID, 'circle-color', '#ffffff');
    map.setPaintProperty(LAYER_NODE_CORE_ID, 'circle-opacity', 1.0);
    map.setPaintProperty(LAYER_NODE_CORE_ID, 'circle-radius', 1.5);
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[ts:territory:force] applyForcePaint applied', {
      halo: !!map.getLayer(LAYER_HALO_ID),
      glow: !!map.getLayer(LAYER_GLOW_TIGHT_ID),
      lines: !!map.getLayer(LAYER_ID),
      nodeBase: !!map.getLayer(LAYER_NODE_GLOW_BASE_ID),
      nodeCore: !!map.getLayer(LAYER_NODE_CORE_ID),
      lineOpacity: map.getLayer(LAYER_ID) ? map.getPaintProperty(LAYER_ID, 'line-opacity') : 'N/A',
    });
  }
}

function registerNetworkLayers(
  map: maplibregl.Map,
  data: FeatureCollection,
  camera: TerritorialCamera,
  onReady: (() => void) | undefined,
  readyRef: { current: boolean },
  palette: NetworkPalette
): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[ts:territory] registerNetworkLayers', {
      guard: !!map.getLayer(LAYER_ID),
      ts: Date.now(),
    });
  }
  if (map.getLayer(LAYER_ID)) return;
  map.easeTo({
    center: camera.center,
    zoom: camera.zoom,
    pitch: camera.pitch,
    bearing: camera.bearing,
    duration: 800,
  });
  addDataLayers(map, data, palette);

  // FORCE mode: aplica valores literales inmediatamente, sin esperar queryRenderedFeatures.
  // Permite confirmar si el problema es de layers/source/geometría vs expresiones/paint.
  if (FORCE_TERRITORIAL_NETWORK_VISIBLE) {
    applyForcePaint(map);
    readyRef.current = true;
    onReady?.();
    return;
  }

  // eslint-disable-next-line complexity
  const withFadeIn = (): void => {
    // Halo aparece recién al completar el fade de la línea principal —
    // evita el spike visible antes del color.
    // eslint-disable-next-line complexity
    const applyFinalPaint = (): void => {
      if (map.getLayer(LAYER_ID))
        map.setPaintProperty(LAYER_ID, 'line-opacity', palette.lineOpacity);
      if (palette.glowOpacity && map.getLayer(LAYER_GLOW_TIGHT_ID))
        map.setPaintProperty(LAYER_GLOW_TIGHT_ID, 'line-opacity', palette.glowOpacity);
      if (map.getLayer(LAYER_HALO_ID))
        map.setPaintProperty(LAYER_HALO_ID, 'line-opacity', palette.lineOpacityHalo);
      if (palette.nodeGlowBaseOpacity && map.getLayer(LAYER_NODE_GLOW_BASE_ID))
        map.setPaintProperty(
          LAYER_NODE_GLOW_BASE_ID,
          'circle-opacity',
          palette.nodeGlowBaseOpacity
        );
      if (palette.nodeGlowUpOpacity && map.getLayer(LAYER_NODE_GLOW_UP_ID))
        map.setPaintProperty(LAYER_NODE_GLOW_UP_ID, 'circle-opacity', palette.nodeGlowUpOpacity);
      if (palette.nodeCoreOpacity && map.getLayer(LAYER_NODE_CORE_ID))
        map.setPaintProperty(LAYER_NODE_CORE_ID, 'circle-opacity', palette.nodeCoreOpacity);
    };
    fadeInLayers(map, applyFinalPaint);
    onReady?.();
  };
  waitUntilRendered(map, withFadeIn, readyRef);
}

// ─── Scene sync hook ──────────────────────────────────────────────────────────

/**
 * Detecta cambio de estilo base (resolveBaseStyle), hace setStyle y reconstruye
 * terrain + red en el orden correcto después de style.load.
 * Skips en el primer render (mapa aún no existe).
 */
function useSceneSync(
  mapRef: { readonly current: maplibregl.Map | null },
  idleHandleRef: { current: IdleHandle | null },
  featureCollectionRef: { readonly current: FeatureCollection | null },
  sceneModeRef: { readonly current: SceneMode },
  sceneMode: SceneMode
): void {
  const isFirst = useRef(true);
  const prevStyleRef = useRef<string | maplibregl.StyleSpecification>(resolveBaseStyle(sceneMode));
  useEffect(() => {
    if (!STYLE_SWITCH_ENABLED || isFirst.current) {
      isFirst.current = false;
      return;
    }
    const map = mapRef.current;
    if (!map) return;
    const nextStyle = resolveBaseStyle(sceneModeRef.current);
    if (nextStyle === prevStyleRef.current) return;
    prevStyleRef.current = nextStyle;
    idleHandleRef.current?.stop();
    idleHandleRef.current = null;
    void map.once('style.load', (): void => {
      const mode = sceneModeRef.current;
      applyPitchConstraints(map, mode);
      // Terrain primero → hillshade al fondo; red después → queda encima.
      if (mode === 'topographic' || mode === 'satellite' || mode === 'cover')
        ensureTerrain(map, resolveTerrainExaggeration(mode));
      if (mode === 'satellite') applyAtmosphericSatellite(map);
      if (mode === 'cover') applyCoverAtmosphere(map);
      const fc = featureCollectionRef.current;
      if (fc && fc.features.length > 0) {
        const palette = resolveNetworkPalette(mode);
        addDataLayers(map, fc, palette);
        // Fade-in idéntico al de registerNetworkLayers — sin esto las capas
        // quedan en opacity 0 (valor inicial de addDataLayers).
        const syncReadyRef = { current: false };
        // eslint-disable-next-line complexity
        const applyFinalPaint = (): void => {
          if (map.getLayer(LAYER_ID))
            map.setPaintProperty(LAYER_ID, 'line-opacity', palette.lineOpacity);
          if (palette.glowOpacity && map.getLayer(LAYER_GLOW_TIGHT_ID))
            map.setPaintProperty(LAYER_GLOW_TIGHT_ID, 'line-opacity', palette.glowOpacity);
          if (map.getLayer(LAYER_HALO_ID))
            map.setPaintProperty(LAYER_HALO_ID, 'line-opacity', palette.lineOpacityHalo);
          if (palette.nodeGlowBaseOpacity && map.getLayer(LAYER_NODE_GLOW_BASE_ID))
            map.setPaintProperty(
              LAYER_NODE_GLOW_BASE_ID,
              'circle-opacity',
              palette.nodeGlowBaseOpacity
            );
          if (palette.nodeGlowUpOpacity && map.getLayer(LAYER_NODE_GLOW_UP_ID))
            map.setPaintProperty(
              LAYER_NODE_GLOW_UP_ID,
              'circle-opacity',
              palette.nodeGlowUpOpacity
            );
          if (palette.nodeCoreOpacity && map.getLayer(LAYER_NODE_CORE_ID))
            map.setPaintProperty(LAYER_NODE_CORE_ID, 'circle-opacity', palette.nodeCoreOpacity);
        };
        waitUntilRendered(map, () => fadeInLayers(map, applyFinalPaint), syncReadyRef);
      }
      const cam: TerritorialCamera = {
        center: map.getCenter().toArray(),
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
        source: 'persisted',
      };
      idleHandleRef.current = startContinuousIdleMotion(map, cam);
    });
    map.setStyle(nextStyle);
  }, [sceneMode, mapRef, idleHandleRef, featureCollectionRef, sceneModeRef]);
}

// ─── Estructura de Sintaxis Espacial — hook ───────────────────────────────────
//
// Llama a /network/hierarchy?profile=equilibrado — endpoint que usa JOIN real
// (relations: ['edge']) y devuelve combinedPercentile ∈ [0,1] ya calculado por
// NetworkMorphologicalHierarchyService.  No hay cálculo de percentiles en el
// frontend; el backend es la fuente de verdad.
//
// Nota: El endpoint /syntax/metrics?metric=... (slim) NO funciona como fuente
// porque TypeORM 0.3.x omite edge_id del SELECT cuando se usa select + loadRelationIds
// (bug confirmado: response tiene solo { value } sin edgeId).

interface HierarchySlimFeature {
  properties: {
    edgeId: string;
    combinedPercentile: number;
  };
}

interface HierarchySlimResponse {
  features: HierarchySlimFeature[];
}

/**
 * Carga syntaxStructureScores desde el endpoint de jerarquía morfológica.
 * Retorna Map<edgeId, combinedPercentile> donde combinedPercentile ∈ [0,1].
 * Fallback silencioso: Map vacío si el proyecto no tiene métricas o hay error.
 */
function useEntrySyntaxStructureScores(
  projectId: string | null,
  token: string | null
): { scores: Map<string, number> } {
  const [scores, setScores] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (!projectId || !token) return;
    const controller = new AbortController();

    fetch(`${SYNTAX_API_URL}/projects/${projectId}/network/hierarchy?profile=equilibrado`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`network/hierarchy ${r.status}`);
        return r.json() as Promise<HierarchySlimResponse>;
      })
      // eslint-disable-next-line complexity
      .then(resp => {
        if (!resp.features || resp.features.length === 0) {
          setScores(new Map());
          return;
        }
        const map = new Map<string, number>();
        for (const f of resp.features) {
          const { edgeId, combinedPercentile } = f.properties;
          if (edgeId && typeof combinedPercentile === 'number') {
            map.set(edgeId, combinedPercentile);
          }
        }
        if (DEBUG_TERRITORIAL_SYNTAX && import.meta.env.DEV) {
          const vals = Array.from(map.values());
          // eslint-disable-next-line no-console
          console.debug('[ts:territory:syntax] hierarchy scores loaded', {
            total: map.size,
            min: vals.length > 0 ? Math.min(...vals).toFixed(3) : 'N/A',
            max: vals.length > 0 ? Math.max(...vals).toFixed(3) : 'N/A',
          });
        }
        setScores(map);
      })
      .catch((_: unknown) => {
        setScores(new Map());
      });

    return (): void => {
      controller.abort();
    };
  }, [projectId, token]);

  return { scores };
}

// ─── DEV lifecycle diagnostics ───────────────────────────────────────────────
// Llamadas solo dentro de `if (import.meta.env.DEV)` — no-ops en producción.
/* eslint-disable no-console */
function devSetMap(map: maplibregl.Map): void {
  (window as unknown as Record<string, unknown>)['__westeerMap'] = map;
  console.debug('[ts:territory] MAP MOUNT', { ts: Date.now() });
  map.on('sourcedata', (e: maplibregl.MapSourceDataEvent) => {
    if (e.sourceId !== SOURCE_ID || !e.isSourceLoaded) return;
    console.debug('[ts:territory] sourcedata:net loaded', {
      src: !!map.getSource(SOURCE_ID),
      lyr: !!map.getLayer(LAYER_ID),
    });
  });
}
function devClearMap(map: maplibregl.Map): void {
  const w = window as unknown as Record<string, unknown>;
  if (w['__westeerMap'] === map) delete w['__westeerMap'];
  console.debug('[ts:territory] MAP UNMOUNT', {
    ts: Date.now(),
    src: !!map.getSource(SOURCE_ID),
    lyr: !!map.getLayer(LAYER_ID),
  });
}
function devLogAfterAdd(map: maplibregl.Map, ts0: number, featureCount: number): void {
  console.debug('[ts:territory] addDataLayers', {
    ts: ts0,
    featureCount,
    src: !!map.getSource(SOURCE_ID),
    halo: !!map.getLayer(LAYER_HALO_ID),
    lines: !!map.getLayer(LAYER_ID),
    color: map.getPaintProperty(LAYER_ID, 'line-color'),
    width: map.getPaintProperty(LAYER_ID, 'line-width'),
    opacity: map.getPaintProperty(LAYER_ID, 'line-opacity'),
    zoom: map.getZoom(),
    center: map.getCenter().toArray(),
  });
}

/** Loguea paint/layout de una layer territorial individual. Extracción para reducir complejidad. */
function devLogSingleLayer(map: maplibregl.Map, id: string, posInStack: number): void {
  const layer = map.getLayer(id);
  if (!layer) {
    console.debug(`[ts:territory:vis] ${id}: NOT PRESENT`);
    return;
  }
  const visibility = map.getLayoutProperty(id, 'visibility') ?? 'visible(default)';
  const props: Record<string, unknown> = { type: layer.type, visibility, posInStack };
  if (layer.type === 'line') {
    props['line-opacity'] = map.getPaintProperty(id, 'line-opacity');
    props['line-width'] = map.getPaintProperty(id, 'line-width');
    props['line-color'] = map.getPaintProperty(id, 'line-color');
  } else if (layer.type === 'circle') {
    props['circle-opacity'] = map.getPaintProperty(id, 'circle-opacity');
    props['circle-radius'] = map.getPaintProperty(id, 'circle-radius');
    props['circle-color'] = map.getPaintProperty(id, 'circle-color');
  } else if (layer.type === 'background') {
    props['background-color'] = map.getPaintProperty(id, 'background-color');
  }
  if (layer.minzoom !== undefined) props['minzoom'] = layer.minzoom;
  if (layer.maxzoom !== undefined) props['maxzoom'] = layer.maxzoom;
  console.debug(`[ts:territory:vis] ${id}:`, props);
}

/** Calcula distribución de syntaxStructureScore de las features enriquecidas. */
function devCalcScoreDistribution(data: FeatureCollection): Record<string, unknown> {
  let withScore = 0;
  let withNull = 0;
  let minScore = Infinity;
  let maxScore = -Infinity;
  let sumScore = 0;
  for (const f of data.features) {
    const s = f.properties?.['syntaxStructureScore'];
    if (typeof s === 'number') {
      withScore++;
      if (s < minScore) minScore = s;
      if (s > maxScore) maxScore = s;
      sumScore += s;
    } else {
      withNull++;
    }
  }
  return {
    total: data.features.length,
    withScore,
    withNull,
    min: withScore > 0 ? Number(minScore.toFixed(3)) : 'N/A',
    max: withScore > 0 ? Number(maxScore.toFixed(3)) : 'N/A',
    avg: withScore > 0 ? Number((sumScore / withScore).toFixed(3)) : 'N/A',
  };
}

/**
 * Diagnóstico completo de visibilidad — loguea estado de cada layer territorial,
 * orden de capas, cámara, features renderizadas y distribución de scores.
 * Solo se ejecuta si DEBUG_TERRITORIAL_NETWORK_VISIBILITY es true y env es DEV.
 */
function devLogNetworkVisibility(map: maplibregl.Map, data: FeatureCollection): void {
  const territorialLayerIds = [
    LAYER_SAT_ATMOSPHERE_ID,
    LAYER_HILLSHADE_ID,
    LAYER_HALO_ID,
    LAYER_GLOW_TIGHT_ID,
    LAYER_ID,
    LAYER_NODE_GLOW_BASE_ID,
    LAYER_NODE_GLOW_UP_ID,
    LAYER_NODE_CORE_ID,
  ];
  const allLayerIds = (map.getStyle()?.layers ?? []).map(l => l.id);
  console.debug(
    '[ts:territory:vis] Layer stack (territorial):',
    allLayerIds.filter(id => id.startsWith('territorial-'))
  );
  console.debug('[ts:territory:vis] Total layers in style:', allLayerIds.length);
  for (const id of territorialLayerIds) devLogSingleLayer(map, id, allLayerIds.indexOf(id));
  console.debug('[ts:territory:vis] Camera:', {
    center: map
      .getCenter()
      .toArray()
      .map(n => Number(n.toFixed(4))),
    zoom: Number(map.getZoom().toFixed(2)),
    pitch: Number(map.getPitch().toFixed(1)),
    bearing: Number(map.getBearing().toFixed(1)),
  });
  console.debug(
    '[ts:territory:vis] Rendered lines (snapshot):',
    map.queryRenderedFeatures({ layers: [LAYER_ID] }).length
  );
  console.debug('[ts:territory:vis] syntaxStructureScore dist:', devCalcScoreDistribution(data));
}
/* eslint-enable no-console */

// ─── Readiness watcher ───────────────────────────────────────────────────────

/**
 * Espera a que la red sea realmente visible (queryRenderedFeatures > 0) antes
 * de emitir onReady. Escucha sourcedata(loaded) + idle en vez de timeouts fijos.
 * Protegido por readyRef para no emitir más de una vez.
 */
function waitUntilRendered(
  map: maplibregl.Map,
  onReady: (() => void) | undefined,
  readyRef: { current: boolean }
): void {
  if (readyRef.current) return;

  function tryEmit(): void {
    if (readyRef.current) return;
    if (map.queryRenderedFeatures({ layers: [LAYER_ID] }).length === 0) return;
    readyRef.current = true;
    map.off('sourcedata', onSrc);
    map.off('idle', tryEmit);
    perfMark('map:network:ready');
    perfMeasure('constructor→network-ready', 'map:constructor:start', 'map:network:ready');
    onReady?.();
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[ts:territory] ready', {
        rendered: map.queryRenderedFeatures({ layers: [LAYER_ID] }).length,
      });
    }
  }

  function onSrc(e: maplibregl.MapSourceDataEvent): void {
    if (e.sourceId !== SOURCE_ID || !e.isSourceLoaded) return;
    tryEmit();
  }

  map.on('sourcedata', onSrc);
  map.on('idle', tryEmit);

  if (import.meta.env.DEV) {
    setTimeout(() => {
      // Ya emitido — nada que hacer.
      if (readyRef.current) return;
      // Guardar contra map destruído (HMR / remount entre el registro y los 30s).
      try {
        const renderedCount = map.queryRenderedFeatures({ layers: [LAYER_ID] }).length;
        if (renderedCount > 0) {
          // Features visibles pero los eventos idle/sourcedata no retriggerearon tryEmit
          // (causa común: startContinuousIdleMotion mantiene la cámara en movimiento
          // → 'idle' nunca vuelve a dispararse). Emitir ready ahora.
          tryEmit();
        } else {
          // rendered === 0 a los 30s — fallo real: sin datos o sin viewport.
          // eslint-disable-next-line no-console
          console.warn('[ts:territory] READINESS WARN 30s', {
            src: !!map.getSource(SOURCE_ID),
            lyr: !!map.getLayer(LAYER_ID),
            rendered: renderedCount,
          });
        }
      } catch (_: unknown) {
        // map.remove() fue llamado antes de que el timer disparara — normal en HMR.
      }
    }, 30_000);
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

// eslint-disable-next-line max-lines-per-function
export function ProjectTerritorialScene({
  projectId,
  bounds,
  sceneMode,
  onSceneReady,
  onSceneError,
}: ProjectTerritorialSceneProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const idleHandleRef = useRef<IdleHandle | null>(null);
  // En NAVIGATION_MODE: pasar null para evitar fetches pesados (37K edges + hierarchy).
  const { featureCollection, error } = useProjectCanonicalNetworkDisplay(
    NAVIGATION_MODE ? null : projectId,
    bounds
  );
  const { token } = useAuthStore();
  const { scores: syntaxStructureScores } = useEntrySyntaxStructureScores(
    NAVIGATION_MODE ? null : projectId,
    token
  );
  const [navCamera, setNavCamera] = useState<{
    lng: number;
    lat: number;
    zoom: number;
    pitch: number;
    bearing: number;
  } | null>(null);
  const [navAltInput, setNavAltInput] = useState('');
  const altFocusedRef = useRef(false);

  const onSceneReadyRef = useRef(onSceneReady);
  const onSceneErrorRef = useRef(onSceneError);
  const readyEmittedRef = useRef(false);
  onSceneReadyRef.current = onSceneReady;
  onSceneErrorRef.current = onSceneError;

  // Stable refs — leídos dentro de callbacks/effects sin añadir a dep arrays
  const initSceneModeRef = useRef(sceneMode);
  const sceneModeRef = useRef(sceneMode);
  sceneModeRef.current = sceneMode;
  const featureCollectionRef = useRef(featureCollection);
  featureCollectionRef.current = featureCollection;
  const syntaxStructureScoresRef = useRef(syntaxStructureScores);
  syntaxStructureScoresRef.current = syntaxStructureScores;
  const { minLng, minLat, maxLng, maxLat } = bounds;
  const addNetworkLayers = useCallback(
    // eslint-disable-next-line complexity
    (map: maplibregl.Map, data: FeatureCollection): void => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[ts:territory] addNetworkLayers', {
          guard: !!map.getLayer(LAYER_ID),
          styleLoaded: map.isStyleLoaded(),
          ts: Date.now(),
        });
      }
      if (map.getLayer(LAYER_ID)) return;
      const mode = sceneModeRef.current;
      const entryCamera = resolveTerritorialEntryCamera({
        featureCollection: data,
        projectBounds: { minLng, minLat, maxLng, maxLat },
        persistedView: getProjectEntryView(projectId),
      });
      const camera =
        mode === 'satellite'
          ? applyTerritorialCameraPreset(entryCamera)
          : mode === 'cover'
            ? applyCoverCameraPreset(entryCamera)
            : entryCamera;
      applyPitchConstraints(map, mode);
      if (mode === 'topographic' || mode === 'satellite' || mode === 'cover')
        ensureTerrain(map, resolveTerrainExaggeration(mode)); // terrain antes de network
      if (mode === 'satellite') applyAtmosphericSatellite(map); // atmósfera blue-hour
      if (mode === 'cover') applyCoverAtmosphere(map); // atmósfera portada
      // Enriquecer features con syntaxStructureScore = percentil compuesto (equilibrado).
      // Si los scores aún no llegaron: null → coalesce → SYNTAX_STRUCTURE_FALLBACK (0.10).
      // La rampa SAT/COVER tiene opacidad mínima > 0.35 → red visible incluso sin scores.
      const needsEnrichment = mode === 'satellite' || mode === 'cover';
      const enriched: FeatureCollection = needsEnrichment
        ? {
            ...data,
            features: data.features.map(f => ({
              ...f,
              properties: {
                ...f.properties,
                syntaxStructureScore:
                  syntaxStructureScoresRef.current.get(
                    (f.properties?.['edgeId'] as string | undefined) ?? ''
                  ) ?? null,
              },
            })),
          }
        : data;
      if (DEBUG_TERRITORIAL_NETWORK_VISIBILITY && import.meta.env.DEV && needsEnrichment) {
        const scoresAvail = syntaxStructureScoresRef.current.size;
        const enrichedWithScore = enriched.features.filter(
          f => typeof f.properties?.['syntaxStructureScore'] === 'number'
        ).length;
        // eslint-disable-next-line no-console
        console.debug('[ts:territory:vis] Enrichment at addNetworkLayers:', {
          totalFeatures: data.features.length,
          scoresAvailable: scoresAvail,
          enrichedWithScore,
          enrichedWithNull: data.features.length - enrichedWithScore,
          note:
            scoresAvail === 0 ? 'SCORES NOT YET ARRIVED — all null → fallback' : 'scores present',
        });
      }
      registerNetworkLayers(
        map,
        enriched,
        camera,
        onSceneReadyRef.current,
        readyEmittedRef,
        resolveNetworkPalette(mode)
      );
      idleHandleRef.current = startContinuousIdleMotion(map, camera);
    },
    [projectId, minLng, minLat, maxLng, maxLat]
  );

  useSceneSync(mapRef, idleHandleRef, featureCollectionRef, sceneModeRef, sceneMode);

  // Sincroniza el input de altitud con el zoom actual cuando no está enfocado
  useEffect(() => {
    if (NAVIGATION_MODE && navCamera && !altFocusedRef.current) {
      setNavAltInput(String(navCamera.zoom));
    }
  }, [navCamera]);

  // Actualiza fuentes GeoJSON cuando llegan los structure scores después del primer render.
  // Solo en modo satellite — los otros modos no usan syntaxStructureScore.
  // Actualiza tanto la fuente de líneas (SOURCE_ID) como la de nodos (SOURCE_NODES_ID)
  // para que nodeSyntaxStructureScore refleje los scores reales al llegar.
  // eslint-disable-next-line complexity
  useEffect(() => {
    const map = mapRef.current;
    if (!map || syntaxStructureScores.size === 0 || !featureCollection) return;
    if (sceneModeRef.current !== 'satellite' && sceneModeRef.current !== 'cover') return;
    try {
      const rawSrc = map.getSource(SOURCE_ID);
      if (!rawSrc) return;
      const enriched: FeatureCollection = {
        ...featureCollection,
        features: featureCollection.features.map(f => ({
          ...f,
          properties: {
            ...f.properties,
            syntaxStructureScore:
              syntaxStructureScores.get((f.properties?.['edgeId'] as string | undefined) ?? '') ??
              null,
          },
        })),
      };
      (rawSrc as maplibregl.GeoJSONSource).setData(enriched);
      const nodeRawSrc = map.getSource(SOURCE_NODES_ID);
      if (nodeRawSrc) {
        (nodeRawSrc as maplibregl.GeoJSONSource).setData(deriveSatelliteNodes(enriched));
      }
      // En FORCE mode, re-aplicar los valores literales después del setData
      // para evitar que la re-evaluación de expresiones interfiera.
      if (FORCE_TERRITORIAL_NETWORK_VISIBLE) applyForcePaint(map);
      if (DEBUG_TERRITORIAL_NETWORK_VISIBILITY && import.meta.env.DEV) {
        const withScore = enriched.features.filter(
          f => typeof f.properties?.['syntaxStructureScore'] === 'number'
        ).length;
        // eslint-disable-next-line no-console
        console.debug('[ts:territory:vis] Late-arrival setData:', {
          scoresSize: syntaxStructureScores.size,
          featuresTotal: enriched.features.length,
          withScore,
          withNull: enriched.features.length - withScore,
          nodesUpdated: !!nodeRawSrc,
        });
      }
    } catch (_: unknown) {
      // map fue destruido antes de que el effect completara — normal en HMR/remount.
    }
  }, [syntaxStructureScores, featureCollection]);

  // Map initialization — bounds iniciales, cámara anima cuando llegan los datos
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!containerRef.current) return;
    perfMark('map:constructor:start');
    // Pitch y bearing iniciales desde el registro del proyecto o el preset del modo.
    // Evita la transición visible plano→oblicuo al arrancar la escena.
    const initMode = initSceneModeRef.current;
    const registeredView = getProjectEntryView(projectId);
    const initialPitch = registeredView?.pitch ?? resolveInitialPitch(initMode);
    const initialBearing = registeredView?.bearing ?? 0;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: resolveBaseStyle(initMode),
      bounds: [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      fitBoundsOptions: { padding: 60 },
      pitch: initialPitch,
      bearing: initialBearing,
      interactive: true,
      attributionControl: false,
    });
    applyPitchConstraints(map, initMode);
    perfMark('map:constructor:done');
    if (PERF_ENABLED) {
      // eslint-disable-next-line no-console
      console.debug('[ts:perf] initial camera', {
        pitch: initialPitch,
        bearing: initialBearing,
        mode: initMode,
      });
      void map.once('style.load', () => {
        perfMark('map:style-loaded');
        perfMeasure('style load', 'map:constructor:start', 'map:style-loaded');
      });
      void map.once('load', () => {
        perfMark('map:load');
      });
      void map.once('idle', () => {
        perfMark('map:first-idle');
        perfMeasure('constructor→idle', 'map:constructor:start', 'map:first-idle');
      });
    }
    // originalEvent solo existe en eventos iniciados por el usuario (no por easeTo).
    map.on('movestart', e => {
      if (e.originalEvent) {
        idleHandleRef.current?.stop();
        idleHandleRef.current = null;
      }
    });
    mapRef.current = map;
    if (import.meta.env.DEV) devSetMap(map);
    if (NAVIGATION_MODE) {
      const updateNav = (): void => {
        const c = map.getCenter();
        setNavCamera({
          lng: Math.round(c.lng * 100000) / 100000,
          lat: Math.round(c.lat * 100000) / 100000,
          zoom: Math.round(map.getZoom() * 1000) / 1000,
          pitch: Math.round(map.getPitch() * 10) / 10,
          bearing: Math.round(map.getBearing() * 10) / 10,
        });
      };
      map.on('move', updateNav);
      void map.once('load', () => {
        // Aplicar terrain, atmósfera y cámara configurada — igual que en producción
        // pero sin cargar la red de segmentos.
        const mode = initSceneModeRef.current;
        applyPitchConstraints(map, mode);
        if (mode === 'satellite') {
          ensureTerrain(map, resolveTerrainExaggeration(mode));
          applyAtmosphericSatellite(map);
        }
        if (mode === 'cover') {
          ensureTerrain(map, resolveTerrainExaggeration(mode));
          applyCoverAtmosphere(map);
        }
        const entryCamera = resolveTerritorialEntryCamera({
          featureCollection: { type: 'FeatureCollection', features: [] },
          projectBounds: { minLng, minLat, maxLng, maxLat },
          persistedView: getProjectEntryView(projectId),
        });
        const camera =
          mode === 'satellite'
            ? applyTerritorialCameraPreset(entryCamera)
            : mode === 'cover'
              ? applyCoverCameraPreset(entryCamera)
              : entryCamera;
        map.easeTo({
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          center: camera.center as [number, number],
          zoom: camera.zoom,
          pitch: camera.pitch,
          bearing: camera.bearing,
          duration: 1800,
        });
        updateNav();
        onSceneReadyRef.current?.();
      });
      // Mouse libre: drag izquierdo = rotar bearing + pitch (no pan)
      map.dragPan.disable();
      const canvas = map.getCanvas();
      canvas.style.cursor = 'grab';
      let dragging = false;
      let dragX = 0;
      let dragY = 0;
      const onCanvasMouseDown = (e: MouseEvent): void => {
        if (e.button !== 0) return;
        dragging = true;
        dragX = e.clientX;
        dragY = e.clientY;
        canvas.style.cursor = 'grabbing';
      };
      const onWindowMouseMove = (e: MouseEvent): void => {
        if (!dragging) return;
        const dx = e.clientX - dragX;
        const dy = e.clientY - dragY;
        dragX = e.clientX;
        dragY = e.clientY;
        const preset = TERRITORIAL_CAMERA_PRESETS[TERRITORIAL_CAMERA_PRESET];
        const maxP = preset.maxPitch;
        map.jumpTo({
          bearing: map.getBearing() + dx * 0.3,
          pitch: Math.max(0, Math.min(maxP, map.getPitch() - dy * 0.2)),
        });
      };
      const onWindowMouseUp = (): void => {
        dragging = false;
        canvas.style.cursor = 'grab';
      };
      canvas.addEventListener('mousedown', onCanvasMouseDown);
      window.addEventListener('mousemove', onWindowMouseMove);
      window.addEventListener('mouseup', onWindowMouseUp);

      // Teclado: flechas = pan XY, = / - = zoom (altura Z)
      const navKeyDown = (e: KeyboardEvent): void => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
        const PAN_PX = 80;
        const ZOOM_STEP = 0.25;
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            map.panBy([-PAN_PX, 0], { duration: 150 });
            break;
          case 'ArrowRight':
            e.preventDefault();
            map.panBy([PAN_PX, 0], { duration: 150 });
            break;
          case 'ArrowUp':
            e.preventDefault();
            map.panBy([0, -PAN_PX], { duration: 150 });
            break;
          case 'ArrowDown':
            e.preventDefault();
            map.panBy([0, PAN_PX], { duration: 150 });
            break;
          case '=':
          case '+':
            e.preventDefault();
            map.easeTo({ zoom: map.getZoom() + ZOOM_STEP, duration: 200 });
            break;
          case '-':
            e.preventDefault();
            map.easeTo({ zoom: map.getZoom() - ZOOM_STEP, duration: 200 });
            break;
          default:
            break;
        }
      };
      window.addEventListener('keydown', navKeyDown);
      return (): void => {
        canvas.removeEventListener('mousedown', onCanvasMouseDown);
        window.removeEventListener('mousemove', onWindowMouseMove);
        window.removeEventListener('mouseup', onWindowMouseUp);
        window.removeEventListener('keydown', navKeyDown);
        idleHandleRef.current?.stop();
        idleHandleRef.current = null;
        readyEmittedRef.current = false;
        if (import.meta.env.DEV) devClearMap(map);
        map.remove();
        mapRef.current = null;
      };
    }
    return (): void => {
      idleHandleRef.current?.stop();
      idleHandleRef.current = null;
      readyEmittedRef.current = false;
      if (import.meta.env.DEV) devClearMap(map);
      map.remove();
      mapRef.current = null;
    };
    // projectId no se incluye en deps — el mapa no se re-monta al cambiar de proyecto,
    // solo cuando cambian los bounds geográficos del proyecto activo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minLng, minLat, maxLng, maxLat]);

  // Añadir capas / manejar error de fetch
  useEffect(() => {
    if (NAVIGATION_MODE) return; // red desactivada — navegación libre de cámara
    if (error) {
      onSceneErrorRef.current?.();
      return;
    }
    const map = mapRef.current;
    if (!map || !featureCollection) return;
    if (featureCollection.features.length === 0) {
      onSceneErrorRef.current?.();
      return;
    }
    perfMark('data:network-features:received');
    perfMeasure(
      'constructor→data-received',
      'map:constructor:start',
      'data:network-features:received'
    );
    if (map.isStyleLoaded()) addNetworkLayers(map, featureCollection);
    else
      void map.once('load', () => {
        addNetworkLayers(map, featureCollection);
      });
  }, [featureCollection, addNetworkLayers, error]);

  const sceneClass = [
    'ts-map-scene',
    sceneMode === 'satellite' ? 'ts-map-scene--satellite' : '',
    sceneMode === 'cover' ? 'ts-map-scene--cover' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={sceneClass}>
      <div ref={containerRef} className="ts-map-container" />
      {sceneMode === 'cover' && (
        <>
          <div className="ts-cover-city-glow" aria-hidden="true" />
          <div className="ts-cover-warm-grade" aria-hidden="true" />
        </>
      )}
      {NAVIGATION_MODE && navCamera && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            background: 'rgba(10,8,4,0.88)',
            color: '#f0d890',
            padding: '14px 18px',
            borderRadius: 10,
            fontFamily: 'monospace',
            fontSize: 13,
            lineHeight: '1.7',
            minWidth: 280,
            backdropFilter: 'blur(6px)',
            border: '1px solid rgba(196,144,32,0.35)',
          }}
        >
          <div
            style={{
              marginBottom: 6,
              fontSize: 10,
              opacity: 0.55,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            Posición de cámara
          </div>
          <div>
            center:&nbsp; [{navCamera.lng}, {navCamera.lat}]
          </div>
          <div>zoom:&nbsp;&nbsp;&nbsp;&nbsp;{navCamera.zoom}</div>
          <div>pitch:&nbsp;&nbsp;&nbsp;{navCamera.pitch}°</div>
          <div>bearing:&nbsp;{navCamera.bearing}°</div>

          {/* Altura manual */}
          <div
            style={{ marginTop: 10, borderTop: '1px solid rgba(196,144,32,0.20)', paddingTop: 10 }}
          >
            <div
              style={{
                fontSize: 10,
                opacity: 0.55,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: 5,
              }}
            >
              Altura (zoom)
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="number"
                step="0.1"
                value={navAltInput}
                onChange={e => {
                  setNavAltInput(e.target.value);
                }}
                onFocus={() => {
                  altFocusedRef.current = true;
                }}
                onBlur={() => {
                  altFocusedRef.current = false;
                  const z = parseFloat(navAltInput);
                  if (!isNaN(z) && z > 0) mapRef.current?.easeTo({ zoom: z, duration: 400 });
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const z = parseFloat(navAltInput);
                    if (!isNaN(z) && z > 0) mapRef.current?.easeTo({ zoom: z, duration: 400 });
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                style={{
                  flex: 1,
                  width: 0,
                  background: 'rgba(196,144,32,0.12)',
                  border: '1px solid rgba(196,144,32,0.35)',
                  borderRadius: 5,
                  color: '#f0d890',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  padding: '4px 8px',
                  outline: 'none',
                }}
              />
              <button
                onClick={() => {
                  const z = parseFloat(navAltInput);
                  if (!isNaN(z) && z > 0) mapRef.current?.easeTo({ zoom: z, duration: 400 });
                }}
                style={{
                  background: 'rgba(196,144,32,0.22)',
                  border: '1px solid rgba(196,144,32,0.40)',
                  borderRadius: 5,
                  color: '#f0d890',
                  cursor: 'pointer',
                  padding: '4px 10px',
                  fontFamily: 'monospace',
                  fontSize: 13,
                }}
              >
                ↵
              </button>
            </div>
          </div>

          {/* Ayuda teclado */}
          <div style={{ marginTop: 8, fontSize: 10, opacity: 0.38, lineHeight: '1.6' }}>
            drag &nbsp;apuntar · ↑↓←→ mover · = / − altura
          </div>

          <button
            style={{
              marginTop: 10,
              width: '100%',
              padding: '8px 0',
              background: '#c49020',
              color: '#1a0800',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 12,
              fontFamily: 'monospace',
              letterSpacing: '0.06em',
            }}
            onClick={() => {
              const snippet =
                `center: [${navCamera.lng}, ${navCamera.lat}],\n` +
                `zoom: ${navCamera.zoom},\n` +
                `pitch: ${navCamera.pitch},\n` +
                `bearing: ${navCamera.bearing},`;
              void navigator.clipboard.writeText(snippet);
            }}
          >
            COPIAR POSICIÓN
          </button>
        </div>
      )}
    </div>
  );
}
